/**
 * Alerts Dashboard Component
 *
 * Displays system notifications (vmalert) and Suricata IDS/IPS alerts for a specific edge
 */

import { useState, useEffect, useCallback } from 'react';
import { Shield, AlertTriangle, AlertCircle, Ban, Bell, CheckCircle, CheckCircle2, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Clock, Filter, Info, RefreshCw, XCircle } from 'lucide-react';
import { suricataService, alertsService } from '../../services';
import type { SuricataAlert, AlertSummary, Notification as NotificationAPI } from '../../services';
import { formatDate } from '../../utils/formatDate';
import { useSettingsStore } from '../../stores/settingsStore';
import { useTenantStore } from '../../stores/tenantStore';

interface AlertsDashboardProps {
  edgeGuid?: string;
}

type SeverityFilter = 'all' | 1 | 2 | 3;
type ActionFilter = 'all' | 'allowed' | 'blocked';
type TimeRange = 1 | 6 | 24 | 168;
type ActiveSection = 'system' | 'suricata';

interface SystemNotification extends Omit<NotificationAPI, 'created_at' | 'read'> {
  timestamp: Date;
  read: boolean;
}

const PAGE_SIZE = 10;

export default function AlertsDashboard({ edgeGuid }: AlertsDashboardProps) {
  const { getDateFormatSettings } = useSettingsStore();
  const selectedTenantId = useTenantStore((s) => s.selectedTenantId);
  const dateSettings = getDateFormatSettings();

  const [systemNotifications, setSystemNotifications] = useState<SystemNotification[]>([]);
  const [alerts, setAlerts] = useState<SuricataAlert[]>([]);
  const [summary, setSummary] = useState<AlertSummary | null>(null);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('all');
  const [actionFilter, setActionFilter] = useState<ActionFilter>('all');
  const [timeRange, setTimeRange] = useState<TimeRange>(24);
  const [selectedAlert, setSelectedAlert] = useState<SuricataAlert | null>(null);
  const [activeSection, setActiveSection] = useState<ActiveSection>('system');
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedNotificationId, setExpandedNotificationId] = useState<string | null>(null);

  const fetchData = useCallback(async (showRefreshing = false) => {
    if (!edgeGuid) return;
    try {
      if (showRefreshing) setRefreshing(true);
      else setLoading(true);

      const [alertsResponse, summaryResponse, notificationsData] = await Promise.all([
        suricataService.getAlerts(edgeGuid, 200),
        suricataService.getAlertSummary(edgeGuid, timeRange),
        alertsService.getNotifications({ limit: 100, edge_id: edgeGuid }),
      ]);

      setAlerts(alertsResponse.alerts || []);
      setSummary(summaryResponse.summary);
      setSystemNotifications(
        notificationsData.map((n) => ({
          ...n,
          timestamp: new Date(n.created_at),
          read: n.read === 1,
          category: n.category || 'System',
        }))
      );
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [edgeGuid, timeRange, selectedTenantId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [severityFilter, actionFilter, timeRange, activeSection]);

  const filteredAlerts = alerts.filter((alert) => {
    if (severityFilter !== 'all' && alert.severity !== severityFilter) return false;
    if (actionFilter !== 'all' && alert.action !== actionFilter) return false;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filteredAlerts.length / PAGE_SIZE));
  const paginatedAlerts = filteredAlerts.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const getSeverityColor = (severity: number) => {
    switch (severity) {
      case 1: return 'bg-red-100 text-red-800 border-red-200';
      case 2: return 'bg-orange-100 text-orange-800 border-orange-200';
      case 3: return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getSeverityLabel = (severity: number) => {
    switch (severity) {
      case 1: return 'High';
      case 2: return 'Medium';
      case 3: return 'Low';
      default: return 'Unknown';
    }
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'blocked':
      case 'dropped':
        return <Ban className="w-4 h-4 text-red-500" />;
      case 'allowed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      default:
        return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'error': return <XCircle className="w-5 h-5 text-red-600" />;
      case 'warning': return <AlertCircle className="w-5 h-5 text-orange-600" />;
      case 'success': return <CheckCircle2 className="w-5 h-5 text-green-600" />;
      default: return <Info className="w-5 h-5 text-blue-600" />;
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'error': return 'bg-red-50 border-red-200';
      case 'warning': return 'bg-orange-50 border-orange-200';
      case 'success': return 'bg-green-50 border-green-200';
      default: return 'bg-blue-50 border-blue-200';
    }
  };

  const formatTimestamp = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  if (!edgeGuid) {
    return (
      <div className="text-center py-8">
        <Shield className="w-12 h-12 text-gray-300 mx-auto mb-2" />
        <p className="text-gray-500">Select an edge to view alerts</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const unresolvedNotifications = systemNotifications.filter((n) => n.resolved !== 1);
  const resolvedNotifications = systemNotifications.filter((n) => n.resolved === 1);

  const renderNotificationCard = (notification: SystemNotification, isResolved: boolean) => {
    const isExpanded = expandedNotificationId === notification.id;
    return (
      <div
        key={notification.id}
        className={`p-4 border rounded-lg ${isResolved ? 'bg-gray-50 border-gray-200 opacity-75' : getNotificationColor(notification.type)}`}
      >
        <div className="flex items-start gap-3">
          {isResolved ? <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" /> : getNotificationIcon(notification.type)}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className={`font-semibold ${isResolved ? 'text-gray-700' : 'text-gray-900'}`}>{notification.title}</h4>
              {notification.category && (
                <span className="text-xs px-2 py-0.5 bg-white/80 rounded font-medium text-gray-600">
                  {notification.category}
                </span>
              )}
              {isResolved && (
                <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded font-medium">
                  Resolved
                </span>
              )}
            </div>
            <p className={`text-sm ${isResolved ? 'text-gray-600' : 'text-gray-700'}`}>{notification.message}</p>
            <div className="flex items-center gap-3 mt-2">
              <p className="text-xs text-gray-500">
                <Clock className="w-3 h-3 inline mr-1" />
                {formatTimestamp(notification.timestamp)}
              </p>
              <button
                onClick={() => setExpandedNotificationId(isExpanded ? null : notification.id)}
                className="text-xs text-primary-600 hover:text-primary-800 font-medium flex items-center gap-0.5"
              >
                {isExpanded ? 'Hide' : 'Show'} Details
                {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
            </div>

            {/* Expandable Detail */}
            {isExpanded && (
              <div className="mt-3 pt-3 border-t border-gray-200/50 space-y-3">
                {notification.edge_id && (
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Edge ID:</span>{' '}
                    <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">{notification.edge_id}</code>
                  </div>
                )}
                {notification.tenant_id && (
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Tenant ID:</span>{' '}
                    <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">{notification.tenant_id}</code>
                  </div>
                )}
                {notification.alert_fingerprint && (
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Alert Fingerprint:</span>{' '}
                    <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">{notification.alert_fingerprint}</code>
                  </div>
                )}
                {notification.resolved_at && (
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Resolved At:</span>{' '}
                    {formatDate(new Date(notification.resolved_at), dateSettings)}
                  </div>
                )}
                {notification.metadata && Object.keys(notification.metadata).length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Metadata</div>
                    <pre className="text-xs text-gray-700 bg-white/80 p-3 rounded border border-gray-200 overflow-x-auto font-mono leading-relaxed">
                      {JSON.stringify(notification.metadata, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Bell className="w-5 h-5 text-primary-600" />
          <h3 className="text-lg font-semibold text-gray-900">Alerts & Notifications</h3>
        </div>
        <button
          onClick={() => fetchData(true)}
          disabled={refreshing}
          className="btn-secondary inline-flex items-center space-x-2"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Section Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveSection('system')}
            className={`flex items-center space-x-2 py-3 px-1 border-b-2 font-medium text-sm ${
              activeSection === 'system'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Bell className="w-4 h-4" />
            <span>System Notifications</span>
            {unresolvedNotifications.length > 0 && (
              <span className="ml-1 px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700 font-medium">
                {unresolvedNotifications.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveSection('suricata')}
            className={`flex items-center space-x-2 py-3 px-1 border-b-2 font-medium text-sm ${
              activeSection === 'suricata'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Shield className="w-4 h-4" />
            <span>Suricata IDS/IPS</span>
            {summary && summary.total_alerts > 0 && (
              <span className="ml-1 px-2 py-0.5 text-xs rounded-full bg-orange-100 text-orange-700 font-medium">
                {summary.total_alerts}
              </span>
            )}
          </button>
        </nav>
      </div>

      {/* System Notifications Section */}
      {activeSection === 'system' && (
        <div className="space-y-4">
          {systemNotifications.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-2" />
              <p className="text-gray-500">No system notifications for this device</p>
            </div>
          ) : (
            <>
              {unresolvedNotifications.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-700 uppercase tracking-wider">Active</h4>
                  {unresolvedNotifications.map((n) => renderNotificationCard(n, false))}
                </div>
              )}
              {resolvedNotifications.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Resolved</h4>
                  {resolvedNotifications.map((n) => renderNotificationCard(n, true))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Suricata Alerts Section */}
      {activeSection === 'suricata' && (
        <div className="space-y-6">
          {/* Summary Cards */}
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Total Alerts</span>
                  <AlertTriangle className="w-5 h-5 text-gray-400" />
                </div>
                <p className="text-2xl font-bold text-gray-900 mt-2">{summary.total_alerts.toLocaleString()}</p>
              </div>
              <div className="bg-white border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Blocked</span>
                  <Ban className="w-5 h-5 text-red-400" />
                </div>
                <p className="text-2xl font-bold text-red-600 mt-2">{summary.blocked_count.toLocaleString()}</p>
              </div>
              <div className="bg-white border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">High Severity</span>
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                </div>
                <p className="text-2xl font-bold text-red-600 mt-2">{summary.high_severity.toLocaleString()}</p>
              </div>
              <div className="bg-white border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Allowed</span>
                  <CheckCircle className="w-5 h-5 text-green-400" />
                </div>
                <p className="text-2xl font-bold text-green-600 mt-2">{summary.allowed_count.toLocaleString()}</p>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-medium text-gray-700">Filters:</span>
            </div>
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Time:</label>
              <select value={timeRange} onChange={(e) => setTimeRange(parseInt(e.target.value) as TimeRange)} className="input-base text-sm py-1">
                <option value={1}>Last hour</option>
                <option value={6}>Last 6 hours</option>
                <option value={24}>Last 24 hours</option>
                <option value={168}>Last 7 days</option>
              </select>
            </div>
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Severity:</label>
              <select value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value === 'all' ? 'all' : parseInt(e.target.value) as SeverityFilter)} className="input-base text-sm py-1">
                <option value="all">All</option>
                <option value={1}>High</option>
                <option value={2}>Medium</option>
                <option value={3}>Low</option>
              </select>
            </div>
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Action:</label>
              <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value as ActionFilter)} className="input-base text-sm py-1">
                <option value="all">All</option>
                <option value="blocked">Blocked</option>
                <option value="allowed">Allowed</option>
              </select>
            </div>
            <span className="text-sm text-gray-500">
              Showing {filteredAlerts.length} alert{filteredAlerts.length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Alerts Table */}
          <div className="bg-white border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Severity</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Signature</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Destination</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {paginatedAlerts.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                        <CheckCircle2 className="w-12 h-12 mx-auto text-green-500 mb-2" />
                        <p>No Suricata alerts found for the selected filters</p>
                      </td>
                    </tr>
                  ) : (
                    paginatedAlerts.map((alert) => (
                      <tr
                        key={alert.id}
                        onClick={() => setSelectedAlert(alert)}
                        className="hover:bg-gray-50 cursor-pointer"
                      >
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center space-x-1">
                            <Clock className="w-3 h-3 text-gray-400" />
                            <span className="text-sm text-gray-600">{formatDate(new Date(alert.timestamp), dateSettings)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getSeverityColor(alert.severity)}`}>
                            {getSeverityLabel(alert.severity)}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center space-x-1">
                            {getActionIcon(alert.action)}
                            <span className="text-sm capitalize">{alert.action}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="max-w-xs truncate">
                            <span className="text-sm font-medium text-gray-900" title={alert.signature}>{alert.signature}</span>
                            <span className="block text-xs text-gray-500">SID: {alert.signature_id}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-sm text-gray-600">{alert.category}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-sm font-mono text-gray-600">{alert.src_ip}{alert.src_port ? `:${alert.src_port}` : ''}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-sm font-mono text-gray-600">{alert.dst_ip}{alert.dst_port ? `:${alert.dst_port}` : ''}</span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {filteredAlerts.length > PAGE_SIZE && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
                <div className="text-sm text-gray-500">
                  Showing {(currentPage - 1) * PAGE_SIZE + 1}-{Math.min(currentPage * PAGE_SIZE, filteredAlerts.length)} of {filteredAlerts.length}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className="text-sm text-gray-700">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Alert Detail Modal */}
      {selectedAlert && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setSelectedAlert(null)} />
            <div className="inline-block w-full max-w-2xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white rounded-lg shadow-xl">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Shield className="w-5 h-5 text-primary-600" />
                  <h3 className="text-lg font-medium text-gray-900">Alert Details</h3>
                </div>
                <button onClick={() => setSelectedAlert(null)} className="text-gray-400 hover:text-gray-600">
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
              <div className="px-6 py-4 space-y-4">
                <div className="flex items-center space-x-4">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getSeverityColor(selectedAlert.severity)}`}>
                    {getSeverityLabel(selectedAlert.severity)} Severity
                  </span>
                  <div className="flex items-center space-x-1">
                    {getActionIcon(selectedAlert.action)}
                    <span className="text-sm capitalize font-medium">{selectedAlert.action}</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Signature</label>
                  <p className="text-sm text-gray-900">{selectedAlert.signature}</p>
                  <p className="text-xs text-gray-500 mt-1">SID: {selectedAlert.signature_id}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Category</label>
                  <p className="text-sm text-gray-900">{selectedAlert.category}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Source</label>
                    <p className="text-sm font-mono text-gray-900">{selectedAlert.src_ip}{selectedAlert.src_port ? `:${selectedAlert.src_port}` : ''}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Destination</label>
                    <p className="text-sm font-mono text-gray-900">{selectedAlert.dst_ip}{selectedAlert.dst_port ? `:${selectedAlert.dst_port}` : ''}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Protocol</label>
                    <p className="text-sm text-gray-900">{selectedAlert.protocol}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Timestamp</label>
                    <p className="text-sm text-gray-900">{formatDate(new Date(selectedAlert.timestamp), dateSettings)}</p>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 bg-gray-50 flex justify-end">
                <button onClick={() => setSelectedAlert(null)} className="btn-secondary">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
